/* eslint-disable camelcase */
import React, { useContext, useMemo } from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { createSelector } from 'reselect';
import { FiltersContext } from 'app/containers/contexts/filters.context/filters.context';
import { Transition } from 'react-transition-group';
import { useFragment, gql, useReactiveVar } from '@apollo/client';

import {
    CATEGORIES_ORDER,
    CATEGORY_MAIN,
    CATEGORY_MOST_INTRESTING,
    CATEGORY_MOST_INTRESTING_TAG_ID,
    CATEGORY_TITLES,

    isCategoryVisibleForLocation,
    mostInterestingCategoryTitleForABTest,
} from 'app/const/categories';

import { selectDishes } from 'app/utils/dish';


/* CONTEXTS */
import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import { MenuDispatchContext } from 'app/containers/contexts/menu.context';
import { LocaleContext } from 'app/containers/LocaleProvider';
import { AuthContext } from 'app/containers/AuthContainer';
import { DesignContext } from 'app/containers/DesignProvider';
import { Mobile, Desktop } from 'app/components/Responsive';
import BonduelleDesktop from 'app/components/banners/BonduelleDesktop';
import BestSetsMobileSlider from 'app/components/SetsMobileSlider/BestSetsMobileSlider';
// import BestSetsDesktop from 'app/components/banners/BestSetsDesktop/BestSetsDesktop';
import './menu-category-list.scss';
import {
    isDesktop,
} from 'app/utils/resolution';
import { piecewiseBezier } from 'app/utils/smoothScrollAnimationUtils';
import { convertArrayIntoLinkedList } from 'app/utils/covertArrayIntoLinkedList';
import { useRouterState } from 'app/hooks/useRouterState';
import { abTestDataContext } from 'app/containers/contexts/abTest.context';
import { useDisabledDatesSelected } from 'app/hooks';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { useBasketData, useCategoryLocationKey } from 'app/hooks/accessors';
import { PathSegmentContext } from 'app/views/Main/PathSegment.context';
import { CROSSED_PRICE_DISCOUNT_TYPE } from 'app/const/crossedPricesPromo';
import {
    MENU_DISH_DISCOUNT_CONDITIONS_FRAGMENT,
} from '../MobileDish/MobileDishDescription/graphql/menuDishDiscountConditionsFragment.graphql';
import { SetsMobileSlider } from '../SetsMobileSlider';
import MenuCategoryAdapter from '../MenuCategory/MenuCategory';
import { useFlatDishesList, usePromoCompoentPosition } from './hooks';
import { getDishesByC_ID } from './hooks/useFlatDishesList';

const viewportPaddingSelector = createSelector(
    (headerHeight) => headerHeight,
    (headerHeight) => ({ top: headerHeight + 400, bottom: 300 }),
);

const APPEARING_TIMEOUT = 150;

const LAST_STEP_SMOOTH_ANIMATION_DURATION = 650;
const BEZIER_ANIMATION_POINTS = [0, 0.80, 0.85, 0.90, 0.95, 0.96, 0.97, 0.98, 0.99, 0.99, 1];


function getCategoriesLinkedList() {
    const categoriesNodes = document.querySelectorAll('.mainCategoryRoot');

    if (!categoriesNodes) return [];

    const categories = Array.from(categoriesNodes);
    const categoriesLinkedList = convertArrayIntoLinkedList(categories);
    return categoriesLinkedList;
}

const checkIsMenuDishesChanges = (dishes) => {
    const IDs_list = dishes.map((itm) => itm.id);
    const hash = IDs_list.join(':');
    return hash;
};

class MenuCategoryList extends React.Component {
    constructor(props) {
        super(props);

        const { menuDishesQuery, abTestDataContext } = props;

        this.state = {
            isDesktopBannerVisible: true,
            menuDishesQuery,
        };

        this.isProgrammaticScroll = false;
        this.isObserverInited = false;
        this.isMainDishesRenderedTriggered = false;
        this.isScrolledByUser = false;
        this.activeCategoryIndex = 0;
        this.activeCategoryId = null;
        this.dishesContainerRef = React.createRef();
        this.mobileBestSetsRef = React.createRef();

        // BR-1125
        this.isIntersectingOn = !isDesktop();
    }

    componentDidMount() {
        const {
            menuDishesQuery,
            history,
        } = this.props;
        // this.setState({
        //     menuDishesQuery,
        // });

        this.resolveMainDishesLoadedState();

        this.initObserver();
        document.addEventListener('categoryChangeFromSlider', this.handleCategoryChange);
        document.addEventListener('scroll', this.handleUserScrolling);

        const currentSearch = history.location.search || window.location.search;
        const params = queryString.parse(currentSearch);
        const menuCategory = params?.mc;

        if (menuCategory) {
            this.activeCategoryId = menuCategory;
            this.replaceCategoryUrlState(menuCategory);
            const customEvent = new CustomEvent('categoryChangeFromSlider', {
                bubbles: true,
                detail: {
                    activeCategoryId: menuCategory,
                },
            });
            document.dispatchEvent(customEvent);
        }
    }

    componentWillUnmount() {
        this.observer?.disconnect();
        document.removeEventListener('categoryChangeFromSlider', this.handleCategoryChange);
    }

    initObserver() {
        this.observer = new IntersectionObserver(this.handleCategoryIntersection, {
            rootMargin: `-${window.innerHeight / 2 - 20 / 2}px 0px`,
            threshold: 0,
        });
        this.observeCategoriesScrolling();
    }

    handleUserScrolling() {
        if (!this.isScrolledByUser) {
            this.isScrolledByUser = true;
        }
    }

    observeCategoriesScrolling() {
        // В DeliveryMenu didMount срабатывает раньше, поэтому не удается получить категории сразу
        const { isDelivery } = this.props;
        if (isDelivery) return null;

        const addListenersToNodes = () => {
            const categoriesNodes = document.querySelectorAll('.mainCategoryRoot');
            if (categoriesNodes) {
                Array.from(categoriesNodes).forEach((c) => this.observer.observe(c));
            }
            if (categoriesNodes.length) {
                this.isObserverInited = true;
            }
        };

        if (!isDelivery) {
            addListenersToNodes();
            return;
        }
        const interval = setInterval(
            () => {
                if (!this.isObserverInited) {
                    addListenersToNodes();
                    console.log('observer not inited');
                } else {
                    console.log('observer inited');
                    clearInterval(interval);
                }
            },
            500,
        );
    }

    handleCategoryIntersection = (entries) => {
        const {
            menuDispatchContext,
        } = this.props;
        entries.forEach((entry) => {
            const isIntersectionDetecting = !this.isProgrammaticScroll && entry.isIntersecting;
            if (!isIntersectionDetecting) return;

            const categoryId = entry.target.dataset.category_id;

            if (!categoryId) return;
            this.replaceCategoryUrlState(categoryId);

            menuDispatchContext({ type: 'intersectCategory', payload: { categoryId } });

            const categories = getCategoriesLinkedList();
            if (!categories) return;
            const targetCategory = categories.find((e) => e.value.dataset.category_id === categoryId);
            this.activeCategoryIndex = targetCategory.index;
        });
    };

    replaceCategoryUrlState = () => {
        if (isDesktop() && this.isScrolledByUser) return;
        const { history } = this.props;
        const currentParams = queryString.parse(history.location.search);

        delete currentParams.mc;
        const newSearch = queryString.stringify(currentParams);

        const detailPageRegex = /dish\/\d+\//;
        const isDetailPageOpened = detailPageRegex.test(window.location.pathname);
        const isMenu = window.location.pathname.includes('menu');
        if (history && !isDetailPageOpened && isMenu) {
            history?.push(`/menu/?${newSearch}`);
        }
    };

    handleCategoryChange = (event) => {
        const { activeCategoryId } = event.detail;

        this.isProgrammaticScroll = true;
        sessionStorage.setItem('isProgrammaticScroll', 'true');
        const isProgrammaticScrollWaitingTimeout = 800;

        const categoriesLinkedList = getCategoriesLinkedList();
        const targetCategory = categoriesLinkedList.find((category) => category.value.dataset.category_id === activeCategoryId);

        if (targetCategory) {
            this.dispatchScrollingChainAnimation(targetCategory);
            setTimeout(() => {
                this.isProgrammaticScroll = false;
                sessionStorage.setItem('isProgrammaticScroll', 'false');
            }, isProgrammaticScrollWaitingTimeout);
        }
    };

    dispatchScrollingChainAnimation(targetCategory) {
        const { menuDispatchContext } = this.props;
        const isDesktopResolution = isDesktop();

        if (!isDesktopResolution) {
            this.mobileScrollCategoryToTop(targetCategory);
        } else {
            menuDispatchContext({
                type: 'scrollTo',
                payload: {
                    value: this.activeCategoryId,
                    target: 'category',
                },
            });
        }
        this.activeCategoryIndex = targetCategory.index;
    }

    mobileScrollCategoryToTop(targetCategory) {
        const scrollDirection = targetCategory.index > this.activeCategoryIndex
            ? 'down'
            : 'up';

        const mobileOffsetPadding = 120;
        const nextTopOffset = targetCategory.next;
        const prevTopOffset = targetCategory.prev;
        const topOffset = targetCategory.value.getBoundingClientRect().top
            + window.pageYOffset
            - mobileOffsetPadding;

        const currentNeighbour = scrollDirection === 'up'
            ? nextTopOffset
            : prevTopOffset;
        const scrollingToNeighbourDistance = currentNeighbour.value
            ? currentNeighbour.value.getBoundingClientRect().top + window.pageYOffset
            : null;

        if (scrollingToNeighbourDistance) {
            window.scrollTo(0, scrollingToNeighbourDistance);
        }
        this.smoothScrollTo(
            topOffset,
            LAST_STEP_SMOOTH_ANIMATION_DURATION,
            null,
        );
        this.handleUserScrolling();
    }

    smoothScrollTo(y, duration, callback) {
        const startY = window.pageYOffset;
        const diff = y - startY;
        const start = performance.now();

        function step(timestamp) {
            const time = (timestamp - start) / duration;
            const t = Math.min(time, 1);

            const easedT = piecewiseBezier(
                t,
                BEZIER_ANIMATION_POINTS,
            );

            window.scrollTo({ top: startY + diff * easedT });

            if (t < 1) {
                window.requestAnimationFrame(step);
            } else if (callback) {
                callback();
            }
        }

        window.requestAnimationFrame(step);
    }

    /*
    isMobileMenuDishesVisible == false, когда открыты мобильные фильтры
    Все ререндеры при нажатиях на фильтры произойдут после нажатия на "Показать блюда"
*/
    shouldComponentUpdate(nextProps) {
        const { isMobileMenuDishesVisible } = nextProps;

        return isMobileMenuDishesVisible;
    }

    componentDidUpdate(prevProps) {
        this.updateDishesAnimationState(prevProps);
        this.resolveMainDishesLoadedState();
    }

    // eslint-disable-next-line
    resolveMainDishesLoadedState() {
        const {
            menuDishesQuery: { allDishes },
            onMainDishesRendered,
        } = this.props;

        if (this.isMainDishesRenderedTriggered) return;

        const mainDishes = selectDishes(allDishes, null, [], [CATEGORY_MAIN]);

        if (mainDishes.length === 0) {
            onMainDishesRendered(0);
        }

        this.isMainDishesRenderedTriggered = true;

        const availableDishesCount = mainDishes.filter((d) => d.visibleStatus === 1).length;
        onMainDishesRendered(availableDishesCount);
    }

    handleCloseDesktopBanner = () => {
        this.setState({
            isDesktopBannerVisible: false,
        });
    };

    handleCategoryViewportPositionChange = ({ categoryId, viewportState }) => {
        const { onCategoryEntered } = this.props;
        if (viewportState.state === 'entered') onCategoryEntered(categoryId);
    };

    updateDishesAnimationState(prevProps) {
        const {
            isMobileMenuDishesVisible,
            menuDishesQuery,
            setDishesUpdatingState,
            period,
        } = this.props;
        const {
            isMobileMenuDishesVisible: prevIsMobileMenuDishesVisible,
            menuDishesQuery: prevMenuDishesQuery,
            prevPeriod,
        } = prevProps;

        const next_dishes_hash = checkIsMenuDishesChanges(menuDishesQuery.menuDishes);
        const prev_dishes_hash = checkIsMenuDishesChanges(prevMenuDishesQuery.menuDishes);

        const isMenuDishesChanged = next_dishes_hash !== prev_dishes_hash;
        const dishesIds = new Set(menuDishesQuery.filtredDishes.map((d) => d.id));
        const prevDishesIds = new Set(prevMenuDishesQuery.filtredDishes.map((d) => d.id));
        const isMenuDishesFiltred = !isEqual(dishesIds, prevDishesIds);

        const { loading } = menuDishesQuery;
        const { loading: prevIsLoading } = prevMenuDishesQuery;

        // if (isMenuDishesChanged) {
        //     console.log('Dishes updated');
        //     this.setState({
        //         menuDishesQuery,
        //     });
        // }

        /*
        При смене периода сурово перезаписываем стейт
    */
        // if (period !== prevPeriod) {
        //     this.setState({ menuDishesQuery });
        //     return;
        // }

        // if (!loading && prevIsLoading) {
        //     this.setState({ menuDishesQuery });
        //     return;
        // }

        /*
        При закрытии мобильных фильтров показываем новые блюда без анимаций
    */
        // if (isMobileMenuDishesVisible !== prevIsMobileMenuDishesVisible) {
        //     this.setState({ menuDishesQuery });
        //     return;
        // }

        // if (loading) return;

        // if (isMenuDishesFiltred) {
        //     setDishesUpdatingState(true, () => {
        //         setTimeout(() => {
        //             this.setState({ menuDishesQuery });
        //             setTimeout(() => {
        //                 setDishesUpdatingState(false);
        //             }, 400);
        //         }, 400);
        //     });
        // }

        // if (isMenuDishesChanged) {
        //     this.setState({
        //         menuDishesQuery,
        //     });
        // }
    }

    renderDesktopBanner() {
        const {
            period,
            isMenuPromoShown,
            isDelivery,
            openDishDetails,
        } = this.props;

        const {
            isDesktopBannerVisible,
        } = this.state;

        const isShownDesktopNewsBanner = isDesktopBannerVisible && !isDelivery && !isMenuPromoShown;

        if (!isShownDesktopNewsBanner) return null;

        // return (
        //     <NYBannerDesktop onClickClose={this.handleCloseDesktopBanner} />
        // );

        return (
            <BonduelleDesktop
                onClick={openDishDetails}
                onClickClose={this.handleCloseDesktopBanner}
                period={period}
            />
        );
    }

    renderMenuCategoryList(transitionState) {
        const {
            designContext: { headerHeight },

            selectedFilters: {
                selectedCategories,
                selectedCategory,
                selectedTags,
                selectedPeriod,
            },
            isDishesUpdating,
            abTestDataContext,
            localeContext: { locale },
            locationKey,
            history,
            setTemporaryBasketPeriod,
            onSetPeriod,
            isDelivery,
            isSelectedDateInEditableRange,
            filterPeriod,
            openBasket,
            selectedDate,
            thirdDishId,
            fifthDishId,
            sixthDishId,
            seventhDishId,
            updatePromoCompoentPosition,
            intrstDishesIdList,
            isDisabledDatesSelected,
            flatDishesList,
            isCartEmpty,
            segment,
            menuDishesQuery,
            isDisabledDateSelected,
            isPromoCodeActivated,
            discountType,
        } = this.props;

        // const {
        //     menuDishesQuery,
        // } = this.state;

        // TODO: правильно передавать props для Dishes
        const dishesProps = {
            ...omit(this.props, [
                'filterQuery',
                'isFiltersOpen',
                'selectedFilters',
                'authData',
                'selectedFilterPeriod',
            ]),
            useLazyLoading: true,
        };


        const isFilterPeriodSelected = filterPeriod === selectedPeriod;
        const canEditTags = isFilterPeriodSelected ? isSelectedDateInEditableRange : true;

        const enabledCategories = CATEGORIES_ORDER.filter((c) => (
            selectedCategories.includes(c)
            && isCategoryVisibleForLocation(c, locationKey)
        ));

        if (!menuDishesQuery.menuDishes) return null;

        const { loading, error, menuDishes: allDishes } = menuDishesQuery;

        const listClasses = classNames({
            'menu-category-list': true,
            [transitionState]: true,
        });
        const dishesContainerClasses = classNames({
            'menu-category-container': true,
            'is-dishes-updating': isDishesUpdating,
            withoutSlider: isDisabledDatesSelected,
            delivery: segment === 'delivery',
        });

        const bannerClasses = classNames({
            'menu-category-list__banner': true,
            slider: true,
        });

        // const canShowSelections = !isDelivery && selectedTags.length === 0 && canEditTags && selectedPeriod !== '2021-12-29';
        const canShowSelections = !isDelivery && canEditTags && selectedPeriod !== '2021-12-29';

        const mostIntrestingDishesData = {
            loading,
            error,
            menuDishes: selectDishes(
                allDishes,
                null,
                [CATEGORY_MOST_INTRESTING_TAG_ID, ...selectedTags],
                selectedCategories,
            ),
        };

        const isMostIntrestingCategoryShown = CATEGORIES_ORDER.includes(CATEGORY_MOST_INTRESTING)
            && selectedCategories.includes(CATEGORY_MOST_INTRESTING)
            && mostIntrestingDishesData.menuDishes.length > 0;

        const isCrossedPriceCampaignRunning = isPromoCodeActivated && discountType === CROSSED_PRICE_DISCOUNT_TYPE;

        return (
            <div styleName={listClasses}>
                <div
                    styleName={dishesContainerClasses}
                    ref={this.dishesContainerRef}
                >
                    {!isDelivery && !isCrossedPriceCampaignRunning && (
                        <Mobile>
                            {/* list or slider */}
                            <div styleName={bannerClasses} ref={this.mobileBestSetsRef}>
                                <SetsMobileSlider
                                    showTrialSet
                                    isBasketEmpty={isCartEmpty}
                                    history={history}
                                    setTemporaryBasketPeriod={setTemporaryBasketPeriod}
                                    onSetPeriod={onSetPeriod}
                                    openBasket={openBasket}
                                    selectedPeriod={selectedPeriod}
                                    isMenuEmpty={false}
                                />
                            </div>
                        </Mobile>
                    )}
                    {(isDelivery || isCrossedPriceCampaignRunning) && (
                        <div style={{ width: '100%', height: 20 }} />
                    )}
                    {isMostIntrestingCategoryShown && !isDisabledDateSelected && (
                        <div className="mainCategoryRoot" data-category_id={CATEGORY_MOST_INTRESTING}>
                            <MenuCategoryAdapter
                                key={CATEGORY_MOST_INTRESTING}
                                index={1}
                                {...dishesProps}
                                flatDishesList={flatDishesList}
                                headerHeight={headerHeight}
                                menuDishesQuery={mostIntrestingDishesData}
                                categoryId={CATEGORY_MOST_INTRESTING}
                                isCatgoryActive={CATEGORY_MOST_INTRESTING === selectedCategory}
                                isIntersectingOn={this.isIntersectingOn}
                                title={mostInterestingCategoryTitleForABTest[locale][CATEGORY_MOST_INTRESTING]}
                                viewportPadding={viewportPaddingSelector(headerHeight)}
                                onViewportPositionChange={this.handleCategoryViewportPositionChange}
                                isDishesUpdating={isDishesUpdating}
                                locale={locale}
                                isDishesSliderNeeded={canShowSelections}
                                thirdDishId={thirdDishId}
                                sixthDishId={sixthDishId}
                                updatePromoCompoentPosition={updatePromoCompoentPosition}
                            />
                        </div>
                    )}
                    {!isDisabledDateSelected && enabledCategories.map((categoryId, index) => {
                        const menuDishes = getDishesByC_ID(allDishes, categoryId, intrstDishesIdList);

                        if (!menuDishes.length) return null;

                        const title = CATEGORY_TITLES[locale][categoryId];

                        const dishesData = {
                            loading,
                            error,
                            menuDishes,
                        };
                        const isDishesSliderNeeded = (
                            categoryId === CATEGORY_MAIN
                            && !isMostIntrestingCategoryShown
                            && canShowSelections
                        );
                        return (
                            <div className="mainCategoryRoot" data-category_id={categoryId} key={categoryId}>
                                <MenuCategoryAdapter
                                    key={categoryId}
                                    index={isMostIntrestingCategoryShown ? index + 1 : index}
                                    {...dishesProps}
                                    headerHeight={headerHeight}
                                    menuDishesQuery={dishesData}
                                    categoryId={categoryId}
                                    isCatgoryActive={categoryId === selectedCategory}
                                    isUserInABTest={this.isUserInABTest}
                                    title={title}
                                    viewportPadding={viewportPaddingSelector(headerHeight)}
                                    onViewportPositionChange={this.handleCategoryViewportPositionChange}
                                    isDishesUpdating={isDishesUpdating}
                                    locale={locale}
                                    isDishesSliderNeeded={isDishesSliderNeeded}
                                    enabledCategories={enabledCategories}
                                    thirdDishId={thirdDishId}
                                    fifthDishId={fifthDishId}
                                    sixthDishId={sixthDishId}
                                    seventhDishId={seventhDishId}
                                    updatePromoCompoentPosition={updatePromoCompoentPosition}
                                />
                            </div>
                        );
                    })}
                    <Desktop>
                        <div styleName="menu-category-list__banner">
                            {this.renderDesktopBanner()}
                        </div>
                    </Desktop>
                </div>
            </div>
        );
    }

    render() {
        return (
            <Transition
                in
                appear
                timeout={APPEARING_TIMEOUT}
            >
                {(transitionState) => this.renderMenuCategoryList(transitionState)}
            </Transition>
        );
    }
}


// [1, 2, 3, 4, 5, 6]

const CART_DATA_FOR_CATEGORY_LIST_QUERY = gql`
    query CART_DATA_FOR_CATEGORY_LIST(
        $period: String!
    ) {
        cart(
            period: $period
        ) {
            type
            period
            sections {
                id
                cartType
            }
        }
    }
`;


const MenuCategoryListAdapter = (props) => {
    const {
        openBasket = null,
        isFiltersOpen = false,
        isMenuPromoShown = false,
        isDelivery = false,
        onMainDishesRendered = () => { },
    } = props;

    const period = useReactiveVar(selectedPeriodVar);
    const isDisabledDateSelected = useDisabledDatesSelected();

    const { data: { isPromoCodeActivated, discountType } } = useFragment({
        fragment: MENU_DISH_DISCOUNT_CONDITIONS_FRAGMENT,
        from: {
            __ref: `DiscountConditions:${period}`,
        },
    });

    const {
        flatDishesList,
        dishesHashSum,
        intrstDishesIdList,
    } = useFlatDishesList(props);

    const {
        thirdDishId,
        fifthDishId,
        sixthDishId,
        seventhDishId,
        ninthDishId,
        updatePromoCompoentPosition,
    } = usePromoCompoentPosition({ flatDishesList, dishesHashSum });

    const { cart } = useBasketData(CART_DATA_FOR_CATEGORY_LIST_QUERY);

    const [routerState] = useRouterState();
    const { selectedFilters } = useContext(FiltersContext);
    const { history, location } = routerState;

    const {
        state: {
            eeAvailableDates, firstVisibleDates, filterPeriod, prevSelectedFilterPeriod,
            selectedFilterPeriod,
            isSelectedDateInEditableRange,
            selectedDate,
        },
    } = useContext(menuDatesState);
    const menuDispatchContext = useContext(MenuDispatchContext);
    const localeContext = useContext(LocaleContext);
    const abTestData = useContext(abTestDataContext);
    const designContextValue = useContext(DesignContext);
    const authData = useContext(AuthContext);
    const locationKey = useCategoryLocationKey();

    const isDisabledDatesSelected = useDisabledDatesSelected();

    // SL
    const isCartEmpty = useMemo(
        () => cart?.sections?.length > 0 || true,
        [cart?.sections?.length],
    );

    const { segment } = useContext(PathSegmentContext);

    return (
        <MenuCategoryList
            {...props}
            isPromoCodeActivated={isPromoCodeActivated}
            discountType={discountType}
            isDisabledDateSelected={isDisabledDateSelected}
            history={history}
            flatDishesList={flatDishesList}
            selectedFilters={selectedFilters}
            location={location}
            locationKey={locationKey}
            localeContext={localeContext}
            abTestDataContext={abTestData}
            menuDispatchContext={menuDispatchContext}
            designContext={designContextValue}
            authData={authData}
            eeAvailableDates={eeAvailableDates}
            firstVisibleDates={firstVisibleDates}
            filterPeriod={filterPeriod}
            selectedDate={selectedDate}
            prevSelectedFilterPeriod={prevSelectedFilterPeriod}
            selectedFilterPeriod={selectedFilterPeriod}
            isSelectedDateInEditableRange={isSelectedDateInEditableRange}
            openBasket={openBasket}
            isFiltersOpen={isFiltersOpen}
            isMenuPromoShown={isMenuPromoShown}
            isDelivery={isDelivery}
            onMainDishesRendered={onMainDishesRendered}
            thirdDishId={thirdDishId}
            fifthDishId={fifthDishId}
            sixthDishId={sixthDishId}
            seventhDishId={seventhDishId}
            ninthDishId={ninthDishId}
            updatePromoCompoentPosition={updatePromoCompoentPosition}
            intrstDishesIdList={intrstDishesIdList}
            isDisabledDatesSelected={isDisabledDatesSelected}
            period={period}
            isCartEmpty={isCartEmpty}
            segment={segment}
        />
    );
};

export default MenuCategoryListAdapter;


MenuCategoryList.propTypes = {
    period: PropTypes.string.isRequired,
    designContext: PropTypes.shape({
        headerHeight: PropTypes.number.isRequired,
    }).isRequired,

    localeContext: PropTypes.shape({
        locale: PropTypes.string.isRequired,
    }).isRequired,

    locationKey: PropTypes.string.isRequired,

    history: PropTypes.shape({}).isRequired,

    isMenuPromoShown: PropTypes.bool,
    isFiltersOpen: PropTypes.bool,
    eeAvailableDates: PropTypes.bool,

    selectedFilterPeriod: PropTypes.string,
    filterPeriod: PropTypes.string.isRequired,

    filterQuery: PropTypes.shape({
        menuFilter: PropTypes.shape({
            periods: PropTypes.arrayOf(PropTypes.shape({})),
        }),
    }).isRequired,

    menuDishesQuery: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.shape({}),
        menuDishes: PropTypes.arrayOf(PropTypes.shape({})),
        allDishes: PropTypes.arrayOf(PropTypes.shape({})),
        filtredDishes: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    authData: PropTypes.shape({
        isAuthed: PropTypes.bool,
    }).isRequired,

    onCategoryEntered: PropTypes.func.isRequired,
    openBasket: PropTypes.func,
    openDishDetails: PropTypes.func.isRequired,

    isDelivery: PropTypes.bool,
    onMainDishesRendered: PropTypes.func,

    setDishesUpdatingState: PropTypes.func.isRequired,
    setTemporaryBasketPeriod: PropTypes.func.isRequired,
    onSetPeriod: PropTypes.func.isRequired,
    isDishesUpdating: PropTypes.bool.isRequired,
    firstVisibleDates: PropTypes.shape({
        eeAvailableDates: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    isSelectedDateInEditableRange: PropTypes.bool.isRequired,
    isMobileMenuDishesVisible: PropTypes.bool.isRequired,
};
